// Renders a slim strip at the top of the page on any non-production
// deployment. Production builds skip the render entirely (so there's no
// DOM and no CSS impact on the live site). The value comes from
// VITE_VERCEL_ENV which is piped from Vercel's VERCEL_ENV in
// vite.config.ts — preview deployments get 'preview', local `npm run
// dev` gets '' (no Vercel env), production builds get 'production'.
//
// Also shows a separate state when the FeatureSection "preview
// bypass" flag is set in localStorage (`?preview=1`). That bypass
// forces every section to render as live even when /admin/flags has
// it hidden, which surprises editors who don't realise it's sticky.
// We surface the state here with a one-click "Turn off" so it's
// never a mystery.

import { useEffect, useState } from 'react';
import { useElectionPhase } from '../lib/useElectionPhase';

const ENV = (import.meta.env.VITE_VERCEL_ENV as string | undefined) ?? '';
const PREVIEW_BYPASS_KEY = 'lm-preview-bypass';

function readBypass(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(PREVIEW_BYPASS_KEY) === '1';
  } catch {
    return false;
  }
}

export default function DevBanner() {
  const [bypass, setBypass] = useState<boolean>(readBypass);
  // Pull the current election phase so we can surface it on
  // non-production banners. The hook fail-safes to 'normal' on
  // loading / error so the chip never blocks the banner from rendering.
  const { phase } = useElectionPhase();

  // Re-read on focus / route change so editing flags in another tab
  // doesn't desync the indicator.
  useEffect(() => {
    function refresh() {
      setBypass(readBypass());
    }
    window.addEventListener('focus', refresh);
    window.addEventListener('popstate', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('popstate', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  function disableBypass() {
    try {
      window.localStorage.removeItem(PREVIEW_BYPASS_KEY);
    } catch {
      /* ignore */
    }
    setBypass(false);
    // Reload so every <FeatureSection> re-evaluates its flag.
    window.location.reload();
  }

  // When the bypass is on, it overrides /admin/flags. That deserves
  // a louder, action-able banner — and it should appear on production
  // too, since the bypass can be triggered there.
  if (bypass) {
    return (
      <div
        role="status"
        aria-label="Preview bypass active"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 9999,
          width: '100%',
          background: '#D4830A',
          color: '#fff',
          fontFamily: "'Poppins', system-ui, sans-serif",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          padding: '8px 14px',
          textAlign: 'center',
          boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <span>PREVIEW BYPASS ON</span>
        <span style={{ opacity: 0.9, fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
          every section rendered as if live — /admin/flags toggles are ignored on this browser
        </span>
        <button
          type="button"
          onClick={disableBypass}
          style={{
            background: '#fff',
            color: '#D4830A',
            border: 'none',
            borderRadius: 4,
            padding: '4px 12px',
            fontFamily: 'inherit',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.8,
            cursor: 'pointer',
            textTransform: 'uppercase',
          }}
        >
          Turn off
        </button>
      </div>
    );
  }

  if (ENV === 'production') return null;

  const label = ENV === 'preview' ? 'PREVIEW DEPLOY' : 'LOCAL DEV';
  const hint =
    ENV === 'preview'
      ? 'this is not the live site — changes here do not affect production'
      : 'running locally — not the live site';

  return (
    <div
      role="status"
      aria-label={label}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        width: '100%',
        background: ENV === 'preview' ? '#7B2D8E' : '#0A8F8F',
        color: '#fff',
        fontFamily: "'Poppins', system-ui, sans-serif",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        padding: '6px 14px',
        textAlign: 'center',
        boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
      }}
    >
      <span style={{ marginRight: 10 }}>{label}</span>
      <span style={{ opacity: 0.85, fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
        {hint}
      </span>
      {/* Phase indicator — verification aid for editors flipping the
          election phase on a preview deploy. Never rendered on
          production (this whole branch is gated by ENV !== 'production'). */}
      <span
        style={{
          marginLeft: 14,
          padding: '2px 8px',
          borderRadius: 4,
          background: 'rgba(255,255,255,0.18)',
          fontWeight: 800,
          letterSpacing: 1,
        }}
        aria-label={`Election phase: ${phase}`}
      >
        PHASE: {phase.toUpperCase()}
      </span>
    </div>
  );
}
